'use client';

import type {
  MysteryRoomAnchorKind,
  MysteryRoomDoorDefinition,
  MysteryRoomIncidentAnchorDefinition,
  MysteryRoomIncidentAnchorKind,
  MysteryRoomInteractableDefinition,
  MysteryRoomHazardDefinition,
  MysteryRoomLightDefinition,
  MysteryRoomMapDefinition,
  MysteryRoomNavEdgeDefinition,
  MysteryRoomNavNodeDefinition,
  MysteryRoomOccluderDefinition,
  MysteryRoomPatrolRouteDefinition,
  MysteryRoomPropDefinition,
  MysteryRoomRect,
  MysteryRoomSegment,
  MysteryRoomWorkAnchorDefinition,
  MysteryRoomZoneDefinition,
} from './mapTypes';
import { MYSTERY_ROOM_STANDARD_AMBIENT_SOUND_DEFINITIONS } from './ambientLibrary';
import {
  MYSTERY_ROOM_FLASHLIGHT_ASSETS,
  MYSTERY_ROOM_GLOCK19_ASSETS,
  getMysteryRoomAccessCardVisual,
} from './itemAssets';

const AIRLOCK_FLOOR = rect(220, 960, 620, 440);
const RESEARCH_FLOOR = rect(920, 160, 1360, 780);
const MED_FLOOR = rect(2440, 200, 1220, 700);
const RESEARCH_ENTRY_FLOOR = rect(1500, 940, 180, 20);
const MED_ENTRY_FLOOR = rect(2960, 900, 180, 60);
const HALL_HORIZONTAL_FLOOR = rect(840, 960, 2820, 300);
const HALL_VERTICAL_FLOOR = rect(1880, 960, 340, 1180);
const SERVER_FLOOR = rect(980, 1380, 900, 760);
const REACTOR_FLOOR = rect(2220, 1340, 1280, 820);

const WALLS: MysteryRoomSegment[] = [
  ...shellSegments(AIRLOCK_FLOOR, { right: [[1080, 1220]] }),
  ...shellSegments(RESEARCH_FLOOR, { bottom: [[1500, 1680]] }),
  ...shellSegments(MED_FLOOR, { bottom: [[2960, 3140]] }),
  ...shellSegments(SERVER_FLOOR, { right: [[1680, 1840]] }),
  ...shellSegments(REACTOR_FLOOR, { left: [[1640, 1800]] }),
  { start: point(840, 960), end: point(1500, 960) },
  { start: point(1680, 960), end: point(2960, 960) },
  { start: point(3140, 960), end: point(3660, 960) },
  { start: point(840, 1260), end: point(1880, 1260) },
  { start: point(2220, 1260), end: point(3660, 1260) },
  { start: point(840, 960), end: point(840, 1080) },
  { start: point(840, 1220), end: point(840, 1260) },
  { start: point(3660, 960), end: point(3660, 1260) },
  { start: point(1880, 1260), end: point(1880, 1680) },
  { start: point(1880, 1840), end: point(1880, 2140) },
  { start: point(2220, 1260), end: point(2220, 1640) },
  { start: point(2220, 1800), end: point(2220, 2140) },
  { start: point(1880, 2140), end: point(2220, 2140) },
];

const OCCLUDERS: MysteryRoomOccluderDefinition[] = [
  occluder('research_island', rect(1310, 360, 520, 260), true, true),
  occluder('med_platform', rect(2860, 360, 420, 250), true, true),
  occluder('server_rack_a', rect(1140, 1490, 130, 460), true, true),
  occluder('server_rack_b', rect(1450, 1490, 130, 460), true, true),
  occluder('reactor_core', rect(2720, 1490, 430, 330), true, true),
  occluder('reactor_pipe_bank', rect(3190, 1450, 170, 520), true, true),
];

const ROOMS: MysteryRoomZoneDefinition[] = [
  room('airlock_entry', 'Входной шлюз', 'public', [AIRLOCK_FLOOR], 0x4b5563, point(530, 1020)),
  room('research_lab', 'Исследовательская лаборатория', 'lab', [RESEARCH_FLOOR, RESEARCH_ENTRY_FLOOR], 0xcbd5e1, point(1600, 230)),
  room('med_bay', 'Медблок', 'lab', [MED_FLOOR, MED_ENTRY_FLOOR], 0xe2e8f0, point(3060, 260)),
  room('server_room', 'Серверная', 'server', [SERVER_FLOOR], 0x312e81, point(1420, 1450)),
  room('reactor_room', 'Реакторный блок', 'reactor', [REACTOR_FLOOR], 0x3f1d1d, point(2800, 1400)),
  room('central_cross', 'Центральный коридор', 'public', [HALL_HORIZONTAL_FLOOR, HALL_VERTICAL_FLOOR], 0x1f2937, point(2048, 1100)),
];

const DOORS: MysteryRoomDoorDefinition[] = [
  door('door_airlock_to_cross', 'airlock_entry', 'central_cross', 'public', rect(820, 1080, 40, 140), rect(760, 1040, 160, 220), 'vertical'),
  door('door_cross_to_research', 'central_cross', 'research_lab', 'lab', rect(1500, 940, 180, 40), rect(1440, 900, 300, 150), 'horizontal', {
    accessMode: 'public',
  }),
  door('door_cross_to_med', 'central_cross', 'med_bay', 'lab', rect(2960, 900, 180, 40), rect(2900, 860, 300, 150), 'horizontal', {
    accessMode: 'public',
  }),
  door('door_cross_to_server', 'central_cross', 'server_room', 'server', rect(1880, 1680, 40, 160), rect(1820, 1620, 160, 280), 'vertical', {
    accessMode: 'card_required',
    accessCardObjectIds: ['research_server_badge'],
  }),
  door('door_cross_to_reactor', 'central_cross', 'reactor_room', 'reactor', rect(2220, 1640, 40, 160), rect(2160, 1580, 160, 280), 'vertical', {
    accessMode: 'card_required',
    accessCardObjectIds: ['server_reactor_badge'],
  }),
];

const INTERACTABLES: MysteryRoomInteractableDefinition[] = [
  interactable('airlock_stun_baton', 'pickup', 'airlock_entry', point(600, 1110), 90, 'Электрошоковая дубинка', {
    itemId: 'stun_baton',
  }),
  interactable('airlock_flashlight', 'pickup', 'airlock_entry', point(500, 1070), 90, 'Фонарик', {
    itemId: 'flashlight',
    inventoryIconAssetUrl: MYSTERY_ROOM_FLASHLIGHT_ASSETS.inventoryUrl,
    inventoryIconAssetName: MYSTERY_ROOM_FLASHLIGHT_ASSETS.inventoryName,
    inventoryIconAssetMimeType: MYSTERY_ROOM_FLASHLIGHT_ASSETS.mimeType,
  }),
  interactable('airlock_pistol', 'pickup', 'airlock_entry', point(660, 1040), 90, 'Пистолет', {
    itemId: 'pistol',
    inventoryIconAssetUrl: MYSTERY_ROOM_GLOCK19_ASSETS.inventoryUrl,
    inventoryIconAssetName: MYSTERY_ROOM_GLOCK19_ASSETS.inventoryName,
    inventoryIconAssetMimeType: MYSTERY_ROOM_GLOCK19_ASSETS.mimeType,
  }),
  interactable('airlock_medkit', 'pickup', 'airlock_entry', point(520, 980), 90, 'Аптечка', {
    itemId: 'medkit',
  }),
  interactable('airlock_light_switch', 'light_switch', 'airlock_entry', point(720, 1040), 90, 'Пульт света шлюза'),
  interactable('research_server_badge', 'access_grant', 'research_lab', point(1890, 760), 90, 'Серверный пропуск', {
    grantsAccess: 'server',
    inventoryIconAssetUrl: getMysteryRoomAccessCardVisual('server').inventoryUrl,
    inventoryIconAssetName: getMysteryRoomAccessCardVisual('server').inventoryName,
    inventoryIconAssetMimeType: getMysteryRoomAccessCardVisual('server').mimeType,
  }),
  interactable('research_light_switch', 'light_switch', 'research_lab', point(1060, 850), 90, 'Пульт света лаборатории'),
  interactable('research_sample_case', 'inspect', 'research_lab', point(1260, 700), 90, 'Кейс образца EMB-14'),
  interactable('server_reactor_badge', 'access_grant', 'server_room', point(1710, 1520), 90, 'Реакторный пропуск', {
    requiredAccess: 'server',
    grantsAccess: 'reactor',
    inventoryIconAssetUrl: getMysteryRoomAccessCardVisual('reactor').inventoryUrl,
    inventoryIconAssetName: getMysteryRoomAccessCardVisual('reactor').inventoryName,
    inventoryIconAssetMimeType: getMysteryRoomAccessCardVisual('reactor').mimeType,
  }),
  interactable('server_light_switch', 'light_switch', 'server_room', point(1180, 1420), 90, 'Пульт света серверной', {
    requiredAccess: 'server',
  }),
  interactable('server_audit_terminal', 'inspect', 'server_room', point(1360, 1710), 90, 'Аудит-терминал', {
    requiredAccess: 'server',
  }),
  interactable('med_hide_curtain', 'hide_zone', 'med_bay', point(3370, 760), 100, 'Медицинская ширма'),
  interactable('med_light_switch', 'light_switch', 'med_bay', point(2780, 820), 90, 'Пульт света медблока'),
  interactable('server_hide_cabinet', 'hide_zone', 'server_room', point(1110, 1960), 100, 'Шкаф питания'),
  interactable('reactor_light_switch', 'light_switch', 'reactor_room', point(2360, 1510), 90, 'Пульт света реактора', {
    requiredAccess: 'reactor',
  }),
  interactable('reactor_console', 'inspect', 'reactor_room', point(2500, 1940), 90, 'Реакторная консоль', {
    requiredAccess: 'reactor',
  }),
];

const LIGHTS: MysteryRoomLightDefinition[] = [
  light('airlock_light_main', 'airlock_entry', point(520, 1140), 460, 0x93c5fd, 0.72, 'airlock_light_switch'),
  light('cross_light_west', 'central_cross', point(1300, 1110), 520, 0x67e8f9, 0.58),
  light('cross_light_core', 'central_cross', point(2048, 1120), 620, 0xe2e8f0, 0.5),
  light('cross_light_south', 'central_cross', point(2048, 1760), 560, 0x38bdf8, 0.46),
  light('research_light_main', 'research_lab', point(1620, 540), 660, 0x99f6e4, 0.7, 'research_light_switch'),
  light('med_light_main', 'med_bay', point(3060, 600), 620, 0xbfdbfe, 0.72, 'med_light_switch'),
  light('server_light_rack', 'server_room', point(1360, 1760), 560, 0xa5b4fc, 0.68, 'server_light_switch', true),
  light('reactor_light_core', 'reactor_room', point(2860, 1640), 700, 0xfb7185, 0.62, 'reactor_light_switch', true),
];

const HAZARDS: MysteryRoomHazardDefinition[] = [
  hazard('server_smoke_rack', 'smoke', 'server_room', point(1240, 1880), 170, 0.72),
  hazard('med_steam_bed', 'smoke', 'med_bay', point(3360, 760), 120, 0.46),
  hazard('reactor_sparks_core', 'sparks', 'reactor_room', point(3180, 1560), 160, 0.9),
  hazard('research_sparks_table', 'sparks', 'research_lab', point(1760, 560), 120, 0.54),
];

const PROPS: MysteryRoomPropDefinition[] = [
  prop('airlock_console_left', 'airlock_console', 'airlock_entry', rect(360, 1160, 72, 110), 0x1f2937, 0x38bdf8),
  prop('airlock_lockers', 'locker', 'airlock_entry', rect(690, 990, 220, 72), 0x334155, 0xf8fafc),
  prop('airlock_crates', 'crate_stack', 'airlock_entry', rect(300, 1000, 160, 92), 0x475569, 0xf59e0b),
  prop('airlock_floor_grate', 'floor_grate', 'airlock_entry', rect(470, 1230, 210, 80), 0x0f172a, 0x94a3b8),

  prop('research_bench_north', 'lab_bench', 'research_lab', rect(1090, 310, 520, 96), 0xe5e7eb, 0x38bdf8),
  prop('research_bench_west', 'lab_bench', 'research_lab', rect(1040, 500, 120, 280), 0xe5e7eb, 0x38bdf8),
  prop('research_bench_center', 'lab_bench', 'research_lab', rect(1410, 585, 520, 100), 0xf1f5f9, 0x60a5fa),
  prop('research_microscope_a', 'microscope', 'research_lab', rect(1220, 445, 74, 74), 0x64748b, 0xf8fafc),
  prop('research_microscope_b', 'microscope', 'research_lab', rect(1810, 465, 74, 74), 0x64748b, 0xf8fafc),
  prop('research_sample_tank_a', 'sample_tank', 'research_lab', rect(1990, 310, 96, 180), 0x0f766e, 0x99f6e4),
  prop('research_wall_terminal', 'wall_terminal', 'research_lab', rect(1760, 795, 180, 62), 0x0f172a, 0x22d3ee),

  prop('med_bed_main', 'medical_bed', 'med_bay', rect(2960, 440, 270, 175), 0xcbd5e1, 0x60a5fa),
  prop('med_surgical_lamp', 'surgical_lamp', 'med_bay', rect(3050, 315, 130, 130), 0xe5e7eb, 0xf8fafc),
  prop('med_monitor_left', 'med_monitor', 'med_bay', rect(2700, 520, 95, 130), 0x111827, 0x22d3ee),
  prop('med_monitor_right', 'med_monitor', 'med_bay', rect(3310, 455, 95, 130), 0x111827, 0xfb7185),
  prop('med_floor_grates', 'floor_grate', 'med_bay', rect(2840, 690, 380, 95), 0x0f172a, 0x94a3b8),

  prop('server_rack_wall_a', 'server_rack', 'server_room', rect(1110, 1480, 115, 430), 0x0f172a, 0x38bdf8),
  prop('server_rack_wall_b', 'server_rack', 'server_room', rect(1280, 1480, 115, 430), 0x0f172a, 0x3b82f6),
  prop('server_rack_wall_c', 'server_rack', 'server_room', rect(1450, 1480, 115, 430), 0x111827, 0xef4444),
  prop('server_console_main', 'server_console', 'server_room', rect(1510, 1960, 250, 105), 0x1f2937, 0x67e8f9),
  prop('server_floor_grate_a', 'floor_grate', 'server_room', rect(1220, 1935, 180, 90), 0x020617, 0x475569),
  prop('server_warning_stripe', 'warning_stripe', 'server_room', rect(1020, 1405, 820, 34), 0x0f172a, 0xfacc15),

  prop('reactor_core_machine', 'reactor_core', 'reactor_room', rect(2700, 1490, 455, 350), 0x1f2937, 0xfb7185),
  prop('reactor_turbine', 'turbine', 'reactor_room', rect(2420, 1660, 260, 260), 0x334155, 0xf97316),
  prop('reactor_pipe_north', 'pipe_run', 'reactor_room', rect(3160, 1450, 270, 86), 0x111827, 0x94a3b8),
  prop('reactor_pipe_east', 'pipe_run', 'reactor_room', rect(3260, 1530, 88, 500), 0x111827, 0xfb7185),
  prop('reactor_console_wall', 'wall_terminal', 'reactor_room', rect(2320, 1485, 160, 70), 0x0f172a, 0xfb7185),
  prop('reactor_warning_stripe', 'warning_stripe', 'reactor_room', rect(2240, 2020, 780, 38), 0x0f172a, 0xf97316),

  prop('cross_grate_west', 'floor_grate', 'central_cross', rect(930, 1060, 330, 100), 0x0f172a, 0x475569),
  prop('cross_grate_core', 'floor_grate', 'central_cross', rect(1890, 1060, 315, 100), 0x0f172a, 0x475569),
  prop('cross_grate_south', 'floor_grate', 'central_cross', rect(1920, 1660, 245, 360), 0x0f172a, 0x475569),
  prop('cross_alarm_button', 'wall_terminal', 'central_cross', rect(1995, 1042, 106, 74), 0x111827, 0xef4444),
  prop('cross_wall_terminal', 'wall_terminal', 'central_cross', rect(3480, 1040, 110, 120), 0x111827, 0x38bdf8),
  prop('cross_warning_stripe_a', 'warning_stripe', 'central_cross', rect(1450, 1222, 500, 28), 0x0f172a, 0xfacc15),
  prop('cross_warning_stripe_b', 'warning_stripe', 'central_cross', rect(2200, 1222, 500, 28), 0x0f172a, 0xfacc15),
];

const PATROL_ROUTES: MysteryRoomPatrolRouteDefinition[] = [
  {
    id: 'guard_main_loop',
    nodes: [
      node(2048, 1120, 'central_cross', 450),
      node(1600, 1020, 'central_cross', 120),
      node(1600, 950, 'research_lab', 120),
      node(1600, 720, 'research_lab', 650),
      node(1600, 950, 'research_lab', 120),
      node(1600, 1020, 'central_cross', 120),
      node(2048, 1120, 'central_cross', 450),
      node(3050, 1000, 'central_cross', 120),
      node(3050, 920, 'med_bay', 120),
      node(3050, 640, 'med_bay', 650),
      node(3050, 920, 'med_bay', 120),
      node(3050, 1000, 'central_cross', 120),
      node(2048, 1120, 'central_cross', 450),
      node(1980, 1760, 'central_cross', 120),
      node(1870, 1760, 'server_room', 120),
      node(1600, 1760, 'server_room', 700),
      node(1870, 1760, 'server_room', 120),
      node(1980, 1760, 'central_cross', 120),
      node(2048, 1120, 'central_cross', 500),
    ],
  },
];

const WORK_ANCHORS: MysteryRoomWorkAnchorDefinition[] = [
  workAnchor('guard_checkpoint_cross', 'central_cross', 'patrol_point', 1980, 1100, ['guard']),
  workAnchor('guard_checkpoint_airlock', 'airlock_entry', 'patrol_point', 760, 1150, ['guard']),
  workAnchor('scientist_terminal_a', 'research_lab', 'workstation', 1880, 760, ['scientist']),
  workAnchor('scientist_experiment_a', 'research_lab', 'experiment', 1860, 560, ['scientist']),
  workAnchor('technician_server_console', 'server_room', 'engineer_work', 1740, 1760, ['technician']),
  workAnchor('technician_reactor_console', 'reactor_room', 'engineer_work', 2500, 1940, ['technician']),
  workAnchor('technician_standby', 'central_cross', 'standby', 1980, 1840, ['technician']),
  workAnchor('medic_station', 'med_bay', 'medic_work', 3050, 640, ['medic']),
  workAnchor('medic_research', 'med_bay', 'workstation', 3340, 760, ['medic']),
  workAnchor('operator_cross', 'central_cross', 'operator_work', 2048, 1080, ['operator', 'guard']),
  workAnchor('break_room_cross', 'central_cross', 'break_room', 2048, 1320, ['guard', 'scientist', 'technician', 'medic', 'operator']),
  workAnchor('restroom_airlock', 'airlock_entry', 'restroom', 360, 1060, ['guard', 'scientist', 'technician', 'medic', 'operator']),
  workAnchor('armory_airlock', 'airlock_entry', 'armory', 800, 1020, ['guard', 'operator']),
  workAnchor('server_breakdown_spot', 'server_room', 'breakdown', 1360, 1710, ['technician']),
  workAnchor('evac_airlock', 'airlock_entry', 'evacuation', 480, 1180, ['guard', 'scientist', 'technician', 'medic', 'operator']),
];

const NAV_NODES: MysteryRoomNavNodeDefinition[] = [
  navNode('cross_center', 'central_cross', 2048, 1120, 'hub'),
  navNode('alarm_button_cross', 'central_cross', 2048, 1080, 'incident'),
  navNode('airlock_inner', 'airlock_entry', 760, 1150, 'door'),
  navNode('airlock_exit', 'airlock_entry', 480, 1180, 'evac'),
  navNode('research_door_outer', 'central_cross', 1600, 1020, 'door'),
  navNode('research_door_inner', 'research_lab', 1600, 900, 'door'),
  navNode('research_work', 'research_lab', 1880, 760, 'work'),
  navNode('research_experiment', 'research_lab', 1860, 560, 'work'),
  navNode('med_door_outer', 'central_cross', 3050, 1020, 'door'),
  navNode('med_door_inner', 'med_bay', 3050, 920, 'door'),
  navNode('med_station', 'med_bay', 3050, 640, 'work'),
  navNode('server_door_outer', 'central_cross', 1980, 1760, 'door'),
  navNode('server_door_inner', 'server_room', 1870, 1760, 'door'),
  navNode('server_console', 'server_room', 1740, 1760, 'work'),
  navNode('reactor_door_outer', 'central_cross', 2140, 1760, 'door'),
  navNode('reactor_door_inner', 'reactor_room', 2320, 1760, 'door'),
  navNode('reactor_console', 'reactor_room', 2500, 1940, 'work'),
];

const NAV_EDGES: MysteryRoomNavEdgeDefinition[] = [
  navEdge('e_cross_alarm_button', 'cross_center', 'alarm_button_cross', 1),
  navEdge('e_cross_alarm_button_return', 'alarm_button_cross', 'cross_center', 1),
  navEdge('e_airlock_inner_exit', 'airlock_exit', 'airlock_inner', 4),
  navEdge('e_airlock_inner_exit_return', 'airlock_inner', 'airlock_exit', 4),
  navEdge('e_airlock_cross', 'airlock_inner', 'cross_center', 8, 'door_airlock_to_cross', 'public'),
  navEdge('e_airlock_cross_return', 'cross_center', 'airlock_inner', 8, 'door_airlock_to_cross', 'public'),
  navEdge('e_cross_research_outer', 'cross_center', 'research_door_outer', 6),
  navEdge('e_cross_research_outer_return', 'research_door_outer', 'cross_center', 6),
  navEdge('e_research_door', 'research_door_outer', 'research_door_inner', 3, 'door_cross_to_research', 'lab'),
  navEdge('e_research_door_return', 'research_door_inner', 'research_door_outer', 3, 'door_cross_to_research', 'lab'),
  navEdge('e_research_work', 'research_door_inner', 'research_work', 4),
  navEdge('e_research_work_return', 'research_work', 'research_door_inner', 4),
  navEdge('e_research_experiment', 'research_work', 'research_experiment', 5),
  navEdge('e_research_experiment_return', 'research_experiment', 'research_work', 5),
  navEdge('e_cross_med_outer', 'cross_center', 'med_door_outer', 6),
  navEdge('e_cross_med_outer_return', 'med_door_outer', 'cross_center', 6),
  navEdge('e_med_door', 'med_door_outer', 'med_door_inner', 3, 'door_cross_to_med', 'lab'),
  navEdge('e_med_door_return', 'med_door_inner', 'med_door_outer', 3, 'door_cross_to_med', 'lab'),
  navEdge('e_med_station', 'med_door_inner', 'med_station', 4),
  navEdge('e_med_station_return', 'med_station', 'med_door_inner', 4),
  navEdge('e_cross_server_outer', 'cross_center', 'server_door_outer', 7),
  navEdge('e_cross_server_outer_return', 'server_door_outer', 'cross_center', 7),
  navEdge('e_server_door', 'server_door_outer', 'server_door_inner', 3, 'door_cross_to_server', 'server'),
  navEdge('e_server_door_return', 'server_door_inner', 'server_door_outer', 3, 'door_cross_to_server', 'server'),
  navEdge('e_server_console', 'server_door_inner', 'server_console', 4),
  navEdge('e_server_console_return', 'server_console', 'server_door_inner', 4),
  navEdge('e_cross_reactor_outer', 'cross_center', 'reactor_door_outer', 7),
  navEdge('e_cross_reactor_outer_return', 'reactor_door_outer', 'cross_center', 7),
  navEdge('e_reactor_door', 'reactor_door_outer', 'reactor_door_inner', 3, 'door_cross_to_reactor', 'reactor'),
  navEdge('e_reactor_door_return', 'reactor_door_inner', 'reactor_door_outer', 3, 'door_cross_to_reactor', 'reactor'),
  navEdge('e_reactor_console', 'reactor_door_inner', 'reactor_console', 4),
  navEdge('e_reactor_console_return', 'reactor_console', 'reactor_door_inner', 4),
];

const INCIDENT_ANCHORS: MysteryRoomIncidentAnchorDefinition[] = [
  incidentAnchor('alarm_cross', 'alarm', 'central_cross', 2048, 1080),
  incidentAnchor('fire_server', 'fire', 'server_room', 1360, 1710),
  incidentAnchor('fire_reactor', 'fire', 'reactor_room', 3380, 1710),
  incidentAnchor('door_failure_server', 'door_failure', 'central_cross', 1880, 1760),
  incidentAnchor('reactor_fault_main', 'reactor_fault', 'reactor_room', 2500, 1510),
  incidentAnchor('server_fault_main', 'server_fault', 'server_room', 1360, 1710),
  incidentAnchor('injury_medbay', 'injured_person', 'med_bay', 3330, 760),
];

export const LISAD_9_MAP: MysteryRoomMapDefinition = {
  id: 'lisad_9_night_shift',
  title: 'LISAD-9: Night Shift',
  playableGameModes: ['coop', 'survival', 'stealth'],
  width: 4096,
  height: 2304,
  spawn: point(520, 1180),
  spawnZoneId: 'airlock_entry',
  spawnSlots: [
    { x: 520, y: 1180, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Игрок 1' },
    { x: 580, y: 1220, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Игрок 2' },
    { x: 640, y: 1180, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Игрок 3' },
    { x: 700, y: 1220, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Игрок 4' },
  ],
  visibilityRadius: 760,
  rooms: ROOMS,
  doors: DOORS,
  walls: WALLS,
  occluders: OCCLUDERS,
  interactables: INTERACTABLES,
  workAnchors: WORK_ANCHORS,
  navNodes: NAV_NODES,
  navEdges: NAV_EDGES,
  incidentAnchors: INCIDENT_ANCHORS,
  patrolRoutes: PATROL_ROUTES,
  lights: LIGHTS,
  hazards: HAZARDS,
  props: PROPS,
  customObjects: [],
  triggers: [],
  soundDefinitions: [
    { id: 'footstep_soft', label: 'Шаги по лабораторному полу', kind: 'footstep' },
    { id: 'impact_metal', label: 'Металлический удар', kind: 'impact' },
    { id: 'alarm_siren', label: 'Сирена тревоги', kind: 'alarm' },
    ...MYSTERY_ROOM_STANDARD_AMBIENT_SOUND_DEFINITIONS.map((def) => ({ ...def })),
  ],
  soundSources: [
    {
      id: 'ambient_cross',
      label: 'Фон центрального узла',
      zoneId: 'central_cross',
      position: point(2048, 1120),
      soundId: 'ambient_track_1',
      radius: 980,
      volume: 0.3,
      loop: true,
    },
    {
      id: 'ambient_airlock',
      label: 'Фон шлюза',
      zoneId: 'airlock_entry',
      position: point(520, 1180),
      soundId: 'ambient_track_2',
      radius: 860,
      volume: 0.3,
      loop: true,
    },
    {
      id: 'ambient_research',
      label: 'Фон исследовательского блока',
      zoneId: 'research_lab',
      position: point(1880, 760),
      soundId: 'ambient_track_3',
      radius: 900,
      volume: 0.3,
      loop: true,
    },
    {
      id: 'ambient_server',
      label: 'Фон серверной',
      zoneId: 'server_room',
      position: point(1680, 1780),
      soundId: 'ambient_track_4',
      radius: 920,
      volume: 0.3,
      loop: true,
    },
    {
      id: 'ambient_medbay',
      label: 'Фон медблока',
      zoneId: 'med_bay',
      position: point(3040, 640),
      soundId: 'ambient_track_5',
      radius: 860,
      volume: 0.3,
      loop: true,
    },
    {
      id: 'ambient_reactor',
      label: 'Фон реакторного блока',
      zoneId: 'reactor_room',
      position: point(2920, 1690),
      soundId: 'ambient_track_6',
      radius: 980,
      volume: 0.3,
      loop: true,
    },
  ],
  npcs: [
    {
      id: 'guard_alpha',
      name: 'Оператор охраны',
      role: 'guard',
      spawn: point(2048, 1120),
      routeId: 'guard_main_loop',
      homeZoneId: 'central_cross',
      workAnchorIds: ['guard_checkpoint_cross', 'guard_checkpoint_airlock'],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab', 'server', 'reactor'],
      color: 0x38bdf8,
    },
    {
      id: 'scientist_beta',
      name: 'Научный сотрудник',
      role: 'scientist',
      spawn: point(1880, 760),
      homeZoneId: 'research_lab',
      workAnchorIds: ['scientist_terminal_a', 'scientist_experiment_a'],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab'],
      color: 0xe5e7eb,
    },
    {
      id: 'technician_gamma',
      name: 'Техник смены',
      role: 'technician',
      spawn: point(1740, 1840),
      homeZoneId: 'server_room',
      workAnchorIds: ['technician_server_console', 'technician_reactor_console', 'technician_standby'],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab', 'server', 'reactor'],
      color: 0xf59e0b,
    },
    {
      id: 'medic_delta',
      name: 'Дежурный медик',
      role: 'medic',
      spawn: point(3050, 640),
      homeZoneId: 'med_bay',
      workAnchorIds: ['medic_station', 'medic_research'],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab', 'server', 'reactor'],
      color: 0x34d399,
    },
  ],
};

function point(x: number, y: number) {
  return { x, y };
}

function rect(x: number, y: number, width: number, height: number): MysteryRoomRect {
  return { x, y, width, height };
}

function room(
  id: string,
  title: string,
  accessLevel: MysteryRoomZoneDefinition['accessLevel'],
  floor: MysteryRoomRect[],
  color: number,
  labelPoint: MysteryRoomZoneDefinition['labelPoint'],
): MysteryRoomZoneDefinition {
  return { id, title, accessLevel, floor, color, labelPoint };
}

function door(
  id: string,
  fromZoneId: string,
  toZoneId: string,
  requiredAccess: MysteryRoomDoorDefinition['requiredAccess'],
  panel: MysteryRoomRect,
  trigger: MysteryRoomRect,
  orientation: MysteryRoomDoorDefinition['orientation'],
  extra: Partial<Pick<MysteryRoomDoorDefinition, 'accessMode' | 'accessCardObjectIds' | 'color' | 'label'>> = {},
): MysteryRoomDoorDefinition {
  return { id, label: extra.label ?? id, fromZoneId, toZoneId, requiredAccess, panel, trigger, orientation, ...extra };
}

function workAnchor(
  id: string,
  zoneId: string,
  kind: MysteryRoomAnchorKind,
  x: number,
  y: number,
  allowedRoles: MysteryRoomWorkAnchorDefinition['allowedRoles'],
): MysteryRoomWorkAnchorDefinition {
  return { id, zoneId, kind, position: point(x, y), allowedRoles };
}

function navNode(
  id: string,
  zoneId: string,
  x: number,
  y: number,
  kind: MysteryRoomNavNodeDefinition['kind'],
): MysteryRoomNavNodeDefinition {
  return { id, zoneId, position: point(x, y), kind };
}

function navEdge(
  id: string,
  fromNodeId: string,
  toNodeId: string,
  distanceCost: number,
  doorId?: string,
  requiredAccess?: MysteryRoomNavEdgeDefinition['requiredAccess'],
): MysteryRoomNavEdgeDefinition {
  return { id, fromNodeId, toNodeId, distanceCost, doorId, requiredAccess };
}

function incidentAnchor(
  id: string,
  kind: MysteryRoomIncidentAnchorKind,
  zoneId: string,
  x: number,
  y: number,
): MysteryRoomIncidentAnchorDefinition {
  return { id, kind, zoneId, position: point(x, y) };
}

function occluder(
  id: string,
  rect: MysteryRoomRect,
  blocksMovement: boolean,
  blocksSight: boolean,
): MysteryRoomOccluderDefinition {
  return { id, rect, blocksMovement, blocksSight };
}

function interactable(
  id: string,
  kind: MysteryRoomInteractableDefinition['kind'],
  zoneId: string,
  anchor: MysteryRoomInteractableDefinition['anchor'],
  interactionRadius: number,
  label: string,
  extra: Partial<Pick<
    MysteryRoomInteractableDefinition,
    'requiredAccess' | 'grantsAccess' | 'itemId' | 'inventoryIconAssetUrl' | 'inventoryIconAssetName' | 'inventoryIconAssetMimeType'
  >> = {},
): MysteryRoomInteractableDefinition {
  return {
    id,
    kind,
    zoneId,
    anchor,
    interactionRadius,
    label,
    ...extra,
  };
}

function light(
  id: string,
  zoneId: string,
  position: MysteryRoomLightDefinition['position'],
  radius: number,
  color: number,
  intensity: number,
  switchId?: string,
  flicker = false,
): MysteryRoomLightDefinition {
  return {
    id,
    zoneId,
    position,
    radius,
    color,
    intensity,
    switchId,
    flicker,
  };
}

function hazard(
  id: string,
  kind: MysteryRoomHazardDefinition['kind'],
  zoneId: string,
  position: MysteryRoomHazardDefinition['position'],
  radius: number,
  intensity: number,
): MysteryRoomHazardDefinition {
  return {
    id,
    kind,
    zoneId,
    position,
    radius,
    intensity,
  };
}

function prop(
  id: string,
  kind: MysteryRoomPropDefinition['kind'],
  zoneId: string,
  bounds: MysteryRoomRect,
  color?: number,
  accentColor?: number,
  rotation = 0,
): MysteryRoomPropDefinition {
  return {
    id,
    kind,
    zoneId,
    rect: bounds,
    color,
    accentColor,
    rotation,
  };
}

function node(x: number, y: number, zoneId: string, pauseMs: number) {
  return { x, y, zoneId, pauseMs };
}

function shellSegments(
  bounds: MysteryRoomRect,
  openings: Partial<Record<'top' | 'right' | 'bottom' | 'left', Array<[number, number]>>>,
): MysteryRoomSegment[] {
  return [
    ...splitHorizontal(bounds.x, bounds.x + bounds.width, bounds.y, openings.top),
    ...splitHorizontal(bounds.x, bounds.x + bounds.width, bounds.y + bounds.height, openings.bottom),
    ...splitVertical(bounds.y, bounds.y + bounds.height, bounds.x, openings.left),
    ...splitVertical(bounds.y, bounds.y + bounds.height, bounds.x + bounds.width, openings.right),
  ];
}

function splitHorizontal(
  startX: number,
  endX: number,
  y: number,
  gaps: Array<[number, number]> = [],
): MysteryRoomSegment[] {
  const normalized = [...gaps].sort((a, b) => a[0] - b[0]);
  const segments: MysteryRoomSegment[] = [];
  let cursor = startX;

  for (const [gapStart, gapEnd] of normalized) {
    if (gapStart > cursor) {
      segments.push({ start: point(cursor, y), end: point(gapStart, y) });
    }
    cursor = Math.max(cursor, gapEnd);
  }

  if (cursor < endX) {
    segments.push({ start: point(cursor, y), end: point(endX, y) });
  }

  return segments;
}

function splitVertical(
  startY: number,
  endY: number,
  x: number,
  gaps: Array<[number, number]> = [],
): MysteryRoomSegment[] {
  const normalized = [...gaps].sort((a, b) => a[0] - b[0]);
  const segments: MysteryRoomSegment[] = [];
  let cursor = startY;

  for (const [gapStart, gapEnd] of normalized) {
    if (gapStart > cursor) {
      segments.push({ start: point(x, cursor), end: point(x, gapStart) });
    }
    cursor = Math.max(cursor, gapEnd);
  }

  if (cursor < endY) {
    segments.push({ start: point(x, cursor), end: point(x, endY) });
  }

  return segments;
}
